import { effectScope, ref, watch } from "vue";
import {
  PersistedWizardFlowResult,
  WizardMessage,
  WizardMode,
} from "@/flow/services/wizard.service";

type PersistedWizardSession = {
  scopeKey: string | null;
  sessionId: string | null;
  messages: WizardMessage[];
  showApproval: boolean;
  flowSpec: any;
  mode: WizardMode;
};

type PersistedScopeSession = {
  sessionId: string;
  mode: WizardMode;
};

const WIZARD_SESSION_STORAGE_KEY = "oneteg.wizard.session";
const WIZARD_REOPEN_STORAGE_KEY = "oneteg.wizard.reopen-after-refresh";
const WIZARD_SCOPE_SESSIONS_STORAGE_KEY = "oneteg.wizard.scope-sessions";
const WIZARD_FRESH_SCOPE_STORAGE_KEY = "oneteg.wizard.fresh-scopes";
/**
 * Sessions whose in-chat "open the connection form?" offer the user answered with
 * "Not now". Persisted because the offer is otherwise replayed from the server on
 * every reload — a refusal that came back after each refresh would be nagging. Keyed
 * by session id, and dropped again the moment the user sends another message.
 */
const WIZARD_DECLINED_CONNECTION_STORAGE_KEY =
  "oneteg.wizard.declined-connection-offers";

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function loadPersistedWizardSession(): PersistedWizardSession {
  const storage = getSessionStorage();
  const emptyState: PersistedWizardSession = {
    scopeKey: null,
    sessionId: null,
    messages: [],
    showApproval: false,
    flowSpec: null,
    mode: "wizard",
  };

  if (!storage) {
    return emptyState;
  }

  const rawState = storage.getItem(WIZARD_SESSION_STORAGE_KEY);
  if (!rawState) {
    return emptyState;
  }

  try {
    const parsed = JSON.parse(rawState);
    return {
      scopeKey: typeof parsed?.scopeKey === "string" ? parsed.scopeKey : null,
      sessionId:
        typeof parsed?.sessionId === "string" ? parsed.sessionId : null,
      messages: Array.isArray(parsed?.messages) ? parsed.messages : [],
      showApproval: Boolean(parsed?.showApproval),
      flowSpec: parsed?.flowSpec ?? null,
      mode: parsed?.mode === "chat" ? "chat" : "wizard",
    };
  } catch {
    storage.removeItem(WIZARD_SESSION_STORAGE_KEY);
    return emptyState;
  }
}

function loadPersistedScopeSessions(): Record<string, PersistedScopeSession> {
  const storage = getSessionStorage();
  if (!storage) {
    return {};
  }

  const rawState = storage.getItem(WIZARD_SCOPE_SESSIONS_STORAGE_KEY);
  if (!rawState) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawState);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }

    return Object.entries(parsed).reduce<Record<string, PersistedScopeSession>>(
      (acc, [key, value]) => {
        if (
          value &&
          typeof value === "object" &&
          typeof (value as PersistedScopeSession).sessionId === "string"
        ) {
          acc[key] = {
            sessionId: (value as PersistedScopeSession).sessionId,
            mode:
              (value as PersistedScopeSession).mode === "chat"
                ? "chat"
                : "wizard",
          };
        }
        return acc;
      },
      {}
    );
  } catch {
    storage.removeItem(WIZARD_SCOPE_SESSIONS_STORAGE_KEY);
    return {};
  }
}

function loadFreshWizardScopes(): Record<string, true> {
  const storage = getSessionStorage();
  if (!storage) {
    return {};
  }

  const rawState = storage.getItem(WIZARD_FRESH_SCOPE_STORAGE_KEY);
  if (!rawState) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawState);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }

    return Object.entries(parsed).reduce<Record<string, true>>(
      (acc, [key, value]) => {
        if (value === true) {
          acc[key] = true;
        }
        return acc;
      },
      {}
    );
  } catch {
    storage.removeItem(WIZARD_FRESH_SCOPE_STORAGE_KEY);
    return {};
  }
}

/**
 * Unlike the scope markers above, these are read and written from a click handler,
 * so every storage call is guarded: `getSessionStorage()` only proves the object
 * exists, while `getItem`/`setItem`/`removeItem` can still throw on their own (a
 * blocked or full store, or a private window). A throw here must degrade to "we
 * could not remember the refusal", never interrupt the click.
 */
function loadDeclinedConnectionOffers(): Record<string, true> {
  const storage = getSessionStorage();
  if (!storage) {
    return {};
  }

  let rawState: string | null = null;
  try {
    rawState = storage.getItem(WIZARD_DECLINED_CONNECTION_STORAGE_KEY);
  } catch {
    return {};
  }

  if (!rawState) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawState);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }

    return Object.entries(parsed).reduce<Record<string, true>>(
      (acc, [key, value]) => {
        if (value === true) {
          acc[key] = true;
        }
        return acc;
      },
      {}
    );
  } catch {
    removeDeclinedConnectionOffers(storage);
    return {};
  }
}

function removeDeclinedConnectionOffers(storage: Storage): void {
  try {
    storage.removeItem(WIZARD_DECLINED_CONNECTION_STORAGE_KEY);
  } catch {
    // Storage is unavailable; nothing to clean up.
  }
}

function writeDeclinedConnectionOffers(nextState: Record<string, true>): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  if (Object.keys(nextState).length === 0) {
    removeDeclinedConnectionOffers(storage);
    return;
  }

  try {
    storage.setItem(
      WIZARD_DECLINED_CONNECTION_STORAGE_KEY,
      JSON.stringify(nextState)
    );
  } catch {
    // Storage is unavailable — the refusal just will not survive a reload.
  }
}

/** Record that the user answered "Not now" to this session's connection offer. */
export function markConnectionOfferDeclined(
  targetSessionId: string | null
): void {
  if (!targetSessionId) {
    return;
  }

  const nextState = loadDeclinedConnectionOffers();
  if (nextState[targetSessionId]) {
    return;
  }

  nextState[targetSessionId] = true;
  writeDeclinedConnectionOffers(nextState);
}

/**
 * Forget the refusal. Called when the user sends another message: that is the only
 * way a new offer can arrive, and it is how they ask for the form back.
 */
export function clearConnectionOfferDecline(
  targetSessionId: string | null
): void {
  if (!targetSessionId) {
    return;
  }

  const nextState = loadDeclinedConnectionOffers();
  if (!nextState[targetSessionId]) {
    return;
  }

  delete nextState[targetSessionId];
  writeDeclinedConnectionOffers(nextState);
}

export function isConnectionOfferDeclined(
  targetSessionId: string | null
): boolean {
  if (!targetSessionId) {
    return false;
  }

  return Boolean(loadDeclinedConnectionOffers()[targetSessionId]);
}

function persistScopeSession(
  targetScopeKey: string,
  persistedSessionId: string,
  persistedMode: WizardMode
): void {
  const nextState = loadPersistedScopeSessions();
  nextState[targetScopeKey] = {
    sessionId: persistedSessionId,
    mode: persistedMode,
  };
  clearFreshWizardScopeMarker(targetScopeKey);
  writePersistedScopeSessions(nextState);
}

function loadScopeSession(
  targetScopeKey: string
): PersistedScopeSession | null {
  const nextState = loadPersistedScopeSessions();
  return nextState[targetScopeKey] || null;
}

function writePersistedScopeSessions(
  nextState: Record<string, PersistedScopeSession>
): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  if (Object.keys(nextState).length === 0) {
    storage.removeItem(WIZARD_SCOPE_SESSIONS_STORAGE_KEY);
    return;
  }

  storage.setItem(WIZARD_SCOPE_SESSIONS_STORAGE_KEY, JSON.stringify(nextState));
}

function writeFreshWizardScopes(nextState: Record<string, true>): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  if (Object.keys(nextState).length === 0) {
    storage.removeItem(WIZARD_FRESH_SCOPE_STORAGE_KEY);
    return;
  }

  storage.setItem(WIZARD_FRESH_SCOPE_STORAGE_KEY, JSON.stringify(nextState));
}

function clearFreshWizardScopeMarker(targetScopeKey: string | null): void {
  if (!targetScopeKey) {
    return;
  }

  const nextState = loadFreshWizardScopes();
  if (!nextState[targetScopeKey]) {
    return;
  }

  delete nextState[targetScopeKey];
  writeFreshWizardScopes(nextState);
}

export function buildWizardFlowScopeKey(
  flowId: string,
  flowVersion: number
): string {
  return `flow:${flowId}:${flowVersion}`;
}

const persistedState = loadPersistedWizardSession();
const scopeKey = ref<string | null>(persistedState.scopeKey);
const sessionId = ref<string | null>(persistedState.sessionId);
const messages = ref<WizardMessage[]>(persistedState.messages);
const loading = ref(false);
const showApproval = ref(persistedState.showApproval);
const flowSpec = ref<any>(persistedState.flowSpec);
const mode = ref<WizardMode>(persistedState.mode);

const persistenceScope = effectScope(true);

persistenceScope.run(() => {
  watch(
    [sessionId, messages, showApproval, flowSpec, mode],
    () => {
      const storage = getSessionStorage();
      if (!storage) {
        return;
      }

      const nextState: PersistedWizardSession = {
        scopeKey: scopeKey.value,
        sessionId: sessionId.value,
        messages: messages.value,
        showApproval: showApproval.value,
        flowSpec: flowSpec.value,
        mode: mode.value,
      };

      const hasPersistedState = Boolean(
        nextState.scopeKey ||
          nextState.sessionId ||
          nextState.messages.length ||
          nextState.showApproval ||
          nextState.flowSpec ||
          nextState.mode !== "wizard"
      );

      if (!hasPersistedState) {
        storage.removeItem(WIZARD_SESSION_STORAGE_KEY);
        return;
      }

      storage.setItem(WIZARD_SESSION_STORAGE_KEY, JSON.stringify(nextState));

      if (nextState.scopeKey && nextState.sessionId) {
        persistScopeSession(
          nextState.scopeKey,
          nextState.sessionId,
          nextState.mode
        );
      }
    },
    { deep: true }
  );
});

export function markWizardForReopenAfterRefresh(): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  storage.setItem(WIZARD_REOPEN_STORAGE_KEY, "wizard");
}

export function consumeWizardReopenAfterRefresh(): boolean {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }

  const shouldReopen = storage.getItem(WIZARD_REOPEN_STORAGE_KEY) === "wizard";
  storage.removeItem(WIZARD_REOPEN_STORAGE_KEY);
  return shouldReopen;
}

export function resetPersistedWizardSession(): void {
  scopeKey.value = null;
  sessionId.value = null;
  messages.value = [];
  loading.value = false;
  showApproval.value = false;
  flowSpec.value = null;
  mode.value = "wizard";
}

export function clearWizardConversation(preserveMode = true): void {
  const nextMode = preserveMode ? mode.value : "wizard";
  sessionId.value = null;
  messages.value = [];
  loading.value = false;
  showApproval.value = false;
  flowSpec.value = null;
  mode.value = nextMode;
}

export function initializeWizardSessionScope(
  nextScopeKey: string,
  preserveExisting = false
): void {
  const hasDifferentScope = Boolean(
    scopeKey.value && scopeKey.value !== nextScopeKey
  );

  if (!preserveExisting && hasDifferentScope) {
    resetPersistedWizardSession();
  }

  scopeKey.value = nextScopeKey;

  const scopedSession = loadScopeSession(nextScopeKey);
  const shouldRestoreScopedSession =
    Boolean(scopedSession) &&
    (preserveExisting || nextScopeKey !== "create-flow");
  if (shouldRestoreScopedSession && scopedSession) {
    sessionId.value = scopedSession.sessionId;
    mode.value = scopedSession.mode;
    // Clear stale approval state — will be re-derived from backend on sync
    showApproval.value = false;
    flowSpec.value = null;
  }
}

export function rememberWizardSessionScope(
  targetScopeKey: string,
  persistedSessionId: string,
  persistedMode: WizardMode
): void {
  persistScopeSession(targetScopeKey, persistedSessionId, persistedMode);
}

export function clearRememberedWizardSessionScope(
  targetScopeKey: string | null
): void {
  if (!targetScopeKey) {
    return;
  }

  const nextState = loadPersistedScopeSessions();
  if (!nextState[targetScopeKey]) {
    return;
  }

  delete nextState[targetScopeKey];
  writePersistedScopeSessions(nextState);
}

export function clearCurrentWizardSessionScope(): void {
  clearRememberedWizardSessionScope(scopeKey.value);
}

export function markCurrentWizardScopeForFreshStart(): void {
  if (!scopeKey.value) {
    return;
  }

  clearRememberedWizardSessionScope(scopeKey.value);
  const nextState = loadFreshWizardScopes();
  nextState[scopeKey.value] = true;
  writeFreshWizardScopes(nextState);
}

export function shouldKeepFreshWizardScope(
  targetScopeKey: string | null
): boolean {
  if (!targetScopeKey) {
    return false;
  }

  return Boolean(loadFreshWizardScopes()[targetScopeKey]);
}

export function rememberWizardSessionForFlow(
  flow: Pick<PersistedWizardFlowResult, "id" | "version">,
  persistedSessionId: string,
  persistedMode: WizardMode
): void {
  persistScopeSession(
    buildWizardFlowScopeKey(flow.id, flow.version),
    persistedSessionId,
    persistedMode
  );
}

export function useWizardSession(): {
  sessionId: typeof sessionId;
  messages: typeof messages;
  loading: typeof loading;
  showApproval: typeof showApproval;
  flowSpec: typeof flowSpec;
  mode: typeof mode;
  hydrateSession: (payload: {
    sessionId?: string | null;
    messages?: WizardMessage[];
    mode?: WizardMode;
    showApproval?: boolean;
    flowSpec?: any;
  }) => void;
  resetSession: (preserveMode?: boolean) => void;
} {
  function hydrateSession(payload: {
    sessionId?: string | null;
    messages?: WizardMessage[];
    mode?: WizardMode;
    showApproval?: boolean;
    flowSpec?: any;
  }): void {
    sessionId.value = payload.sessionId ?? null;
    messages.value = payload.messages ? [...payload.messages] : [];
    loading.value = false;
    showApproval.value = Boolean(payload.showApproval);
    flowSpec.value = payload.flowSpec ?? null;
    mode.value = payload.mode === "chat" ? "chat" : "wizard";
  }

  function resetSession(preserveMode = true): void {
    clearWizardConversation(preserveMode);
  }

  return {
    sessionId,
    messages,
    loading,
    showApproval,
    flowSpec,
    mode,
    hydrateSession,
    resetSession,
  };
}
